import { PrimeNgDropdownEvent } from '../../shared/primeng/events/dropdown-event';
import { TranslationService } from 'src/app/shared';
import { UserRead } from 'src/app/users/shared/user-read.model';
import { UserService } from 'src/app/users/shared/user.service';
import { RevenueItemService } from '../../revenue/shared/revenue-items/revenue-item.service';
import { PagedResultGeneric } from '../../shared/model/paged-result.model';
import { ProfileRelationSearch } from '../../profiles/shared/profile-relation-search.model';
import { SelectItem } from 'primeng/api';
import { SalesOpportunityDto } from '../shared/dto/sales-opportunity.dto';
import { ProfileRead } from '../../profiles/shared/profile-read.model';
import { ProfileService } from '../../profiles/shared/profile.service';
import { Component, OnInit, Input, SimpleChanges, OnChanges, Output, EventEmitter, ViewEncapsulation } from '@angular/core';
import { SalesOpportunityService } from '../shared/sales-opportunity.service';

interface DropdownActionItem {
  actionId: any;
  actionLabel: any;
  action: () => any;
}

enum ClosedLostEnum {
  Closed = 'closed',
  Lost = 'lost',
}

@Component({
  selector: 'wl-sales-opportunity-edit',
  templateUrl: './sales-opportunity-edit.component.html',
  styleUrls: ['./sales-opportunity-edit.component.less'],
  encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SalesOpportunityEditComponent implements OnInit, OnChanges {
  @Input()
  get salesOpportunity() {
    return this._salesOpportunity;
  }
  set salesOpportunity(value: SalesOpportunityDto) {
    this._salesOpportunity = value;
    if (value?.ProfileID) {
      this.loadProfileAndRelated(value.ProfileID);
    }
    this.setDefaultProperties();
    this.salesOpportunityChange.emit(this._salesOpportunity);
  }
  @Output() salesOpportunityChange: EventEmitter<SalesOpportunityDto> = new EventEmitter();
  profile: ProfileRead;
  currentUser: UserRead;
  workingWithProfileItems: SelectItem[] = [];
  revenueItems: SelectItem[] = [];
  probabilityItems: SelectItem[] = [];
  sourceItems: SelectItem[] = [];
  assignedToUserItems: SelectItem[] = [];
  lostReasons: SelectItem[] = [];
  closedLostItems: DropdownActionItem[] = [];
  selectedClosedLostItem: DropdownActionItem;

  private _salesOpportunity: SalesOpportunityDto;

  constructor(
    private _profileService: ProfileService,
    private _revenueItemService: RevenueItemService,
    private _salesOpportunityService: SalesOpportunityService,
    private _userService: UserService,
    private _translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this._revenueItemService.getAllRevenueItems().then((items) => {
      this.revenueItems = items.map<SelectItem>((item) => {
        return { label: item.ItemName, value: item.ItemNum };
      });
    });

    this._salesOpportunityService.searchSalesOpportunityProbabilities().then((probabilities) => {
      this.probabilityItems = probabilities.Result.map<SelectItem>((probability) => {
        return { label: probability.Descr, value: probability.Probability };
      });
    });

    this._salesOpportunityService.searchSalesOpportunitySources().then((sources) => {
      this.sourceItems = sources.Result.map<SelectItem>((source) => {
        return { label: source.Descr, value: source.Source };
      });
    });

    this._salesOpportunityService.searchSalesOpportunityLostReasons().then((reasons) => {
      this.lostReasons = reasons.Result.map<SelectItem>((reason) => {
        return { label: reason.Descr, value: reason.LostReason };
      });
    });

    this._userService.getActiveUsers().then((users) => {
      this.assignedToUserItems = users.map<SelectItem>((user) => {
        return { label: user.UserName, value: user.UserNum };
      });
    });

    this._userService.getCurrentUser().then((user) => {
      this.currentUser = user;
    });

    this.initClosedLostItems();
  }

  ngOnChanges(simpleChanges: SimpleChanges) {}

  public initWithProfile(profileId: number) {
    this.loadProfileAndRelated(profileId);
    this.salesOpportunity = { ProfileID: profileId } as SalesOpportunityDto;
  }

  closedLostChange(actionItem?: DropdownActionItem) {
    if (actionItem) {
      actionItem.action();
    } else {
      this.salesOpportunity.Closed = false;
      this.salesOpportunity.Lost = false;
      this.salesOpportunity.ClosedDate = null;
      this.salesOpportunity.LostDate = null;
    }
  }

  workingWithChange(event: PrimeNgDropdownEvent<SelectItem>) {
    if (event.value === null) {
      this.salesOpportunity.WorkingWithProfileID = 0;
    }
  }

  private initClosedLostItems() {
    Promise.all([
      this._translationService.getTitlecase('sales-opportunities.edit.closed'),
      this._translationService.getTitlecase('sales-opportunities.edit.lost'),
    ]).then(([closedLabel, lostLabel]) => {
      this.closedLostItems = [
        {
          actionId: ClosedLostEnum.Closed,
          actionLabel: closedLabel,
          action: () => {
            this.salesOpportunity.Closed = true;
            this.salesOpportunity.Lost = false;
            this.salesOpportunity.ClosedDate = new Date();
            this.salesOpportunity.LostDate = null;
          },
        },
        {
          actionId: ClosedLostEnum.Lost,
          actionLabel: lostLabel,
          action: () => {
            this.salesOpportunity.Closed = false;
            this.salesOpportunity.Lost = true;
            this.salesOpportunity.LostDate = new Date();
            this.salesOpportunity.ClosedDate = null;
          },
        },
      ];
    });
  }

  private loadProfileAndRelated(profileId: number) {
    this._profileService.getProfile(profileId).then((profile) => {
      this.profile = profile;
      this._profileService.getMemberIconColor(profileId).then((color) => {
        this.profile.MemberTypeIconColor = color;
      });
    });
    this._profileService.getProfileViewPrimaryRelations({ ProfileId: profileId, PageSize: 999 } as ProfileRelationSearch).then((result) => {
      this.workingWithProfileItems = result.Result?.map<SelectItem>((profile) => {
        return { label: profile.ReportName, value: profile.ProfileId };
      });
    });
  }

  private setDefaultProperties() {
    if (!this.salesOpportunity) {
      return;
    }
    this.selectedClosedLostItem = null;
    this.salesOpportunity.WorkingWithProfileID = this.salesOpportunity.WorkingWithProfileID ?? 0;
    if (!this.salesOpportunity.SalesID) {
      this.salesOpportunity.AssignedTo = this.currentUser?.UserNum;
      this.salesOpportunity.Started = true;
      this.salesOpportunity.DateStarted = new Date();
      this.salesOpportunity.EstimatedAmount = 0;
      this.salesOpportunity.EsitmatedClose = new Date();
    } else {
      if (this.salesOpportunity.Closed) {
        this.selectedClosedLostItem = this.closedLostItems.find((x) => x.actionId === ClosedLostEnum.Closed);
      } else if (this.salesOpportunity.Lost) {
        this.selectedClosedLostItem = this.closedLostItems.find((x) => x.actionId === ClosedLostEnum.Lost);
      } else {
        this.selectedClosedLostItem = null;
      }
    }
  }
}
